/**
 * Auswertungs-Kennzahlen (Dashboard) als REINE, wiederverwendbare Funktion über dem Event-Log —
 * EINE Quelle für Cloud-Portal, lokale Console und (künftig) PHP-Admin-JS-Insel. Kein I/O.
 * Nutzt die geteilte Projektion (`projectOrders`) statt einer eigenen — keine Divergenz.
 */

import type { EventEnvelope } from "./events.js";
import type { User } from "./model/user.js";
import type { MenuSnapshot } from "./model/menu.js";
import { orderPaidCents, lineTotalCents } from "./model/order.js";
import { projectOrders } from "./orderStats.js";

export interface Stats {
  revenueCents: number;
  tipCents: number;
  orderCount: number; // Bestellungen (nicht storniert, mit Positionen)
  belegCount: number; // Anzahl Zahlungen
  avgBonCents: number;
  topItems: { name: string; qty: number; revenueCents: number }[];
  byWaiter: { name: string; revenueCents: number; orders: number }[];
  byHour: { hour: number; revenueCents: number }[]; // 0..23, nur Stunden mit Umsatz
}

/** Neueste menu-/users-Snapshots (höchste Version) aus den Events ziehen. */
export function latestConfigSnapshots(events: EventEnvelope[]): {
  menu: MenuSnapshot | null;
  users: User[];
} {
  let menu: MenuSnapshot | null = null;
  let menuV = -1;
  let users: User[] = [];
  let usersV = -1;
  for (const env of events) {
    const b = env.body;
    if (b.kind !== "config.snapshot") continue;
    if (b.configKind === "menu" && b.version > menuV) {
      menuV = b.version;
      menu = (b.data as MenuSnapshot) ?? null;
    } else if (b.configKind === "users" && b.version > usersV) {
      usersV = b.version;
      users = (b.data as User[]) ?? [];
    }
  }
  return { menu, users };
}

/** Dashboard-Kennzahlen aus dem Event-Log berechnen (Umsatz, Bons, Ø, Trinkgeld, Top-Artikel,
 *  je Kellner, je Stunde). */
export function dashboardStats(events: EventEnvelope[]): Stats {
  const orders = projectOrders(events);
  const { menu, users } = latestConfigSnapshots(events);
  const nameOf = new Map(users.map((u) => [u.id as string, u.name]));
  // Info-/Notiz-Pseudo-Artikel zählen nicht als Verkauf — aus den Top-Artikeln raus.
  const infoItemIds = new Set((menu?.items ?? []).filter((i) => i.isInfo).map((i) => i.id as string));

  let revenueCents = 0;
  let tipCents = 0;
  let belegCount = 0;
  let orderCount = 0;

  const items = new Map<string, { qty: number; revenueCents: number }>();
  const waiter = new Map<string, { revenueCents: number; orders: number }>();
  const hours = new Array<number>(24).fill(0);

  for (const o of orders) {
    const active = o.lines.filter((l) => l.status !== "voided");
    if (o.status !== "void" && active.length > 0) orderCount++;

    const paid = Number(orderPaidCents(o));
    revenueCents += paid;

    for (const p of o.payments ?? []) {
      belegCount++;
      tipCents += Number(p.tipCents);
      const h = new Date(p.atMs).getHours();
      hours[h] = (hours[h] ?? 0) + Number(p.amountCents);
    }

    for (const l of active) {
      if (l.isInfo || infoItemIds.has(l.menuItemId as string)) continue;
      const cur = items.get(l.name) ?? { qty: 0, revenueCents: 0 };
      cur.qty += l.qty;
      cur.revenueCents += Number(lineTotalCents(l));
      items.set(l.name, cur);
    }

    const w = waiter.get(o.waiterId as string) ?? { revenueCents: 0, orders: 0 };
    w.revenueCents += paid;
    w.orders += 1;
    waiter.set(o.waiterId as string, w);
  }

  const topItems = [...items.entries()]
    .map(([name, v]) => ({ name, qty: v.qty, revenueCents: v.revenueCents }))
    .sort((a, b) => b.qty - a.qty)
    .slice(0, 15);

  const byWaiter = [...waiter.entries()]
    .map(([id, v]) => ({ name: nameOf.get(id) ?? id.slice(0, 8), revenueCents: v.revenueCents, orders: v.orders }))
    .sort((a, b) => b.revenueCents - a.revenueCents);

  const byHour = hours
    .map((revenueCents, hour) => ({ hour, revenueCents }))
    .filter((h) => h.revenueCents > 0);

  return {
    revenueCents,
    tipCents,
    orderCount,
    belegCount,
    avgBonCents: belegCount > 0 ? Math.round(revenueCents / belegCount) : 0,
    topItems,
    byWaiter,
    byHour,
  };
}
